/**
 * Continuing a chat after one of its transfers (`start_transfer`, a storage import) finished.
 *
 * Mirrors the server's wording (omicsai-bioagent chat-api/continuation_invoker.py `_transfer_prompt`)
 * so the follow-up turn says the same, whichever path — this browser or the cloud invoker — wins
 * the continuation claim.
 */

export type TransferOutcomeStatus = 'succeeded' | 'partial' | 'failed'

export interface TransferOutcome {
  status: TransferOutcomeStatus
  /** Destination folder relative to the user's root, '' for the root. */
  destinationPath: string
  copiedFiles: number
  failedFiles: number
}

function fileCount(count: number): string {
  return `${count} ${count === 1 ? 'file' : 'files'}`
}

/**
 * The prompt is also the user bubble the follow-up turn shows, so it is worded as the user asking,
 * not as instructions to the agent. The id stays where the agent needs it to look the transfer up.
 */
export function transferContinuationPrompt(runId: string, outcome: TransferOutcome): string {
  const folder = outcome.destinationPath.replace(/^\/+|\/+$/g, '')
  const destination = folder ? `cloud:${folder}/` : 'cloud:'
  if (outcome.status === 'succeeded') {
    return (
      `My transfer has finished: ${fileCount(outcome.copiedFiles)} now in ${destination}. ` +
      'If I said what I wanted to do with them, please go ahead.'
    )
  }
  if (outcome.status === 'partial') {
    return (
      `My transfer ${runId} finished with errors: ${fileCount(outcome.copiedFiles)} in ${destination}, ` +
      `${outcome.failedFiles} failed. Which files failed and why? Can we retry them?`
    )
  }
  return `My transfer ${runId} failed. What went wrong, and what should I try next?`
}
